import { useMemo } from 'react';

import { amountWithCurrencySymbol, CurrencyCode } from '@/helpers/currency';
import {
  getPoolStatus,
  // PoolStatusEnum,
  type IHitRange,
  type PoolLevelEnum,
} from '@/helpers/jackpot';

import { useExchangeAmount } from './useExchangeAmount';

const getHitRangeStep = (from: number) => {
  if (from < 200) return 5;
  if (from < 2000) return 10;
  return 100;
};

interface IUsePoolDataParams {
  prize: number;
  type: PoolLevelEnum;
  hitRange: IHitRange;
  minBet?: number;
}

export const usePoolData = ({
  prize,
  type,
  hitRange,
  minBet,
}: IUsePoolDataParams) => {
  const {
    getExchangeAmount,
    formatExchangedAmount,
    formatMinBet,
    formatAmount,
    displayCurrency,
  } = useExchangeAmount();

  const formattedPrize = useMemo(
    () => formatExchangedAmount(prize, CurrencyCode.USD, 2),
    [formatExchangedAmount, prize]
  );

  const formattedHitRange = useMemo(() => {
    const fromConverted = getExchangeAmount(hitRange.from);
    const toConverted = getExchangeAmount(hitRange.to);

    let from: number;
    let to: number;

    if (displayCurrency === CurrencyCode.USD) {
      from = fromConverted;
      to = toConverted;
    } else {
      const step = getHitRangeStep(fromConverted);
      from = Math.floor(fromConverted / step) * step;
      to = Math.ceil(toConverted / step) * step;
    }

    return amountWithCurrencySymbol(
      displayCurrency,
      `${formatAmount(from, false)}-${formatAmount(to, false)}`,
      { noDecimals: true }
    );
  }, [hitRange, getExchangeAmount, formatAmount, displayCurrency]);

  const formattedMinBet = useMemo(
    () => (minBet ? formatMinBet(minBet) : null),
    [formatMinBet, minBet]
  );

  const poolStatus = useMemo(() => getPoolStatus(type, prize), [type, prize]);
  // TODO: temp hardcode status for animation testing, replace with real status when animation will be ready
  // const poolStatus = PoolStatusEnum.ABOUT_TO_BLOW;

  return {
    formattedPrize,
    formattedHitRange,
    formattedMinBet,
    poolStatus,
  };
};
